import { API_BASE_URL } from './apiBase';

export interface AdminSession {
  username: string;
  role: 'operator' | 'editor';
  csrfToken: string;
}

let csrfToken: string | null = null;
let installed = false;
const originalFetch = window.fetch.bind(window);

function isApiRequest(url: string): boolean {
  return url.startsWith('/api') || url.includes('/api/');
}

export function installAdminFetch(): void {
  if (installed) return;
  installed = true;
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.href
        : input.url;
    if (!isApiRequest(url)) {
      return originalFetch(input, init);
    }
    const method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
    const headers = new Headers(init?.headers || (input instanceof Request ? input.headers : undefined));
    if (csrfToken && method !== 'GET' && method !== 'HEAD') {
      headers.set('X-CSRF-Token', csrfToken);
    }
    return originalFetch(input, {
      ...init,
      headers,
      credentials: 'include',
    });
  };
}

function rememberSession(session: AdminSession | null): void {
  csrfToken = session?.csrfToken || null;
}

export async function login(username: string, password: string): Promise<{ ok: true; session: AdminSession } | { ok: false; error: string }> {
  const response = await originalFetch(`${API_BASE_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ username, password }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    rememberSession(null);
    return { ok: false, error: body.error || 'Invalid username or password' };
  }
  const session: AdminSession = {
    username: body.username,
    role: body.role,
    csrfToken: body.csrfToken,
  };
  rememberSession(session);
  return { ok: true, session };
}

export async function restoreSession(): Promise<AdminSession | null> {
  const response = await originalFetch(`${API_BASE_URL}/auth/me`, {
    credentials: 'include',
  });
  if (!response.ok) {
    rememberSession(null);
    return null;
  }
  const body = await response.json();
  const session: AdminSession = {
    username: body.username,
    role: body.role,
    csrfToken: body.csrfToken,
  };
  rememberSession(session);
  return session;
}

export async function logout(): Promise<void> {
  try {
    await fetch(`${API_BASE_URL}/auth/logout`, { method: 'POST' });
  } finally {
    rememberSession(null);
  }
}
